import { useState, useEffect, useMemo } from 'react'
import { Alert, Button, Space, Tag, Typography, Spin } from 'antd'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { ExclamationCircleOutlined, CreditCardOutlined } from '@ant-design/icons'
import { getStripe } from '../../utils/stripe'
import billingService from '../../services/billingService'
import './PaymentModal.css'

const { Text } = Typography

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) {
    return ''
  }
  const numericAmount = typeof amount === 'number' ? amount : Number(amount)
  if (Number.isNaN(numericAmount)) {
    return ''
  }
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  })
  return formatter.format(numericAmount)
}

const getResearchDepthLabel = (depth) => {
  const depthLabels = {
    1: 'Shallow (Quick)',
    2: 'Medium',
    3: 'Deep',
    4: 'Very Deep',
    5: 'Maximum (Comprehensive)',
  }
  return depthLabels[depth] || depth
}

const ANALYST_LABELS = {
  market: 'Market Analyst',
  social: 'Social Sentiment',
  news: 'News Analysis',
  fundamentals: 'Fundamental Analysis',
}

const DAYS_OF_WEEK = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const formatTimePart = (value) => String(value).padStart(2, '0')

const formatScheduleSummary = ({
  scheduleType,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleHour,
  scheduleMinute,
}) => {
  if (!scheduleType) return '—'

  const type = scheduleType.toUpperCase()
  const hasRawTime = scheduleHour !== null && scheduleHour !== undefined
    && scheduleMinute !== null && scheduleMinute !== undefined
  const safeHour = hasRawTime ? Number(scheduleHour) : NaN
  const safeMinute = hasRawTime ? Number(scheduleMinute) : NaN
  const hasTime = Number.isFinite(safeHour) && Number.isFinite(safeMinute)
  const timeLabel = hasTime
    ? `${formatTimePart(safeHour)}:${formatTimePart(safeMinute)} (NY)`
    : '—'

  if (type === 'DAILY') {
    return 'Daily'
  }

  if (type === 'WEEKLY') {
    const dayLabel = DAYS_OF_WEEK[scheduleDayOfWeek] || '—'
    return `Weekly on ${dayLabel}`
  }

  if (type === 'MONTHLY') {
    const dayLabel = scheduleDayOfMonth || '—'
    return `Monthly on day ${dayLabel}`
  }

  return scheduleType
}

// Payment Form with PaymentElement (inside Elements provider)
const PaymentForm = ({
  quote,
  subscriptionEstimate,
  selectedAnalysts,
  researchDepth,
  scheduleType,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleHour,
  scheduleMinute,
  onPaymentSuccess,
  loading,
  setLoading,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState(null)
  const [paymentElementReady, setPaymentElementReady] = useState(false)

  const categorizeError = (error) => {
    const errorMsg = error?.message || ''
    const errorType = error?.type || ''

    if (errorType === 'validation_error' || errorMsg.includes('network') || errorMsg.includes('connection')) {
      return {
        type: 'network',
        title: 'Connection Issue',
        message: errorMsg || 'Network connection lost. Please check your internet and try again.',
      }
    }

    if (errorType === 'card_error' || errorMsg.includes('card') || errorMsg.includes('declined')) {
      return {
        type: 'card',
        title: 'Payment Issue',
        message: errorMsg || 'Your payment method was declined. Please try another.',
      }
    }

    return {
      type: 'stripe',
      title: 'Payment Error',
      message: errorMsg || 'Unable to process payment. Please try again.',
    }
  }

  const handleSubmit = async () => {
    console.log('=== handleSubmit called ===')

    if (!stripe || !elements) {
      console.log('Stripe or elements not ready')
      setErrorMessage({
        type: 'stripe',
        title: 'Payment Form Loading',
        message: 'Payment details are still loading. Please wait a moment.',
      })
      return
    }

    console.log('Starting payment processing...')
    setLoading(true)
    setErrorMessage(null)

    try {
      console.log('Confirming SetupIntent...')
      // Confirm the SetupIntent to get the payment method
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })

      console.log('Stripe result:', result)
      const { error, setupIntent } = result

      if (error) {
        console.error('SetupIntent error:', error)
        const categorized = categorizeError(error)
        setErrorMessage(categorized)
        setLoading(false)
        return
      }

      console.log('SetupIntent:', setupIntent)

      // Get the payment method ID from the SetupIntent
      // The structure varies depending on Stripe's response
      let paymentMethodId = setupIntent?.payment_method

      // Sometimes it's nested in different properties
      if (!paymentMethodId && setupIntent) {
        // Check if payment_method is a string or object
        if (typeof setupIntent.payment_method === 'string') {
          paymentMethodId = setupIntent.payment_method
        } else if (setupIntent.payment_method?.id) {
          paymentMethodId = setupIntent.payment_method.id
        }
      }

      console.log('Extracted paymentMethodId:', paymentMethodId)

      if (paymentMethodId) {
        console.log('Calling onPaymentSuccess with:', paymentMethodId)
        await onPaymentSuccess(paymentMethodId)
      } else {
        console.error('No payment method ID found in setupIntent:', setupIntent)
        setErrorMessage({
          type: 'stripe',
          title: 'Payment Error',
          message: 'Unable to retrieve payment method. Please try again.',
        })
        setLoading(false)
      }
    } catch (err) {
      console.error('handleSubmit error:', err)
      const categorized = categorizeError(err)
      setErrorMessage(categorized)
      setLoading(false)
    }
  }

  const analystNames = selectedAnalysts?.map((a) => ANALYST_LABELS[a] || a) || []
  const scheduleSummary = formatScheduleSummary({
    scheduleType,
    scheduleDayOfWeek,
    scheduleDayOfMonth,
    scheduleHour,
    scheduleMinute,
  })

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="payment-form__content">
        {/* Payment Summary */}
        <div className="payment-summary">
          <div className="payment-summary__header">
            <Text className="payment-summary__title">Billing Summary</Text>
            <Tag color="blue" className="payment-summary__badge">
              Secure Payment
            </Tag>
          </div>

          <div className="payment-summary__details">
            <div className="payment-detail-row">
              <Text className="payment-detail-row__label">Research Depth</Text>
              <Text className="payment-detail-row__value">
                {getResearchDepthLabel(researchDepth)}
              </Text>
            </div>
            <div className="payment-detail-row">
              <Text className="payment-detail-row__label">Analysis Agents</Text>
              <Text className="payment-detail-row__value">
                {analystNames.length} agent{analystNames.length !== 1 ? 's' : ''}
              </Text>
            </div>
            <div className="payment-detail-row">
              <Text className="payment-detail-row__label">Schedule</Text>
              <Text className="payment-detail-row__value">{scheduleSummary}</Text>
            </div>
            {subscriptionEstimate && (
              <>
                <div className="payment-detail-row">
                  <Text className="payment-detail-row__label">Discount</Text>
                  <Text className="payment-detail-row__value" style={{ color: '#16a34a' }}>
                    {subscriptionEstimate.discountLabel}
                  </Text>
                </div>
              </>
            )}
          </div>

          <div className="payment-summary__total">
            <Text className="payment-total__label">
              {subscriptionEstimate ? 'Monthly Total' : 'Cost per Execution'}
            </Text>
            <Text className="payment-total__amount">
              {subscriptionEstimate
                ? formatAmount(subscriptionEstimate.estimatedMonthlyAmount)
                : (quote ? formatAmount(quote.totalAmount) : '---')}
            </Text>
          </div>
        </div>

        {/* Payment Method Section with PaymentElement */}
        <div className="payment-method-section">
          <PaymentElement
            options={{ layout: 'tabs' }}
            onReady={() => setPaymentElementReady(true)}
          />
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={errorMessage.title}
            description={
              <Space direction="vertical" size="small">
                <Text>{errorMessage.message}</Text>
              </Space>
            }
            className="payment-error-alert"
          />
        )}

        {/* Submit Button */}
        <div className="payment-actions">
          <Button
            type="primary"
            htmlType="button"
            loading={loading}
            disabled={!stripe || !elements || !paymentElementReady}
            size="large"
            block
            className="payment-btn payment-btn--confirm"
            onClick={handleSubmit}
          >
            {loading ? 'Processing...' : (
              subscriptionEstimate
                ? `Subscribe — ${formatAmount(subscriptionEstimate.estimatedMonthlyAmount)} / month`
                : 'Confirm & Create Scheduled Task'
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}

let stripeInitializationError = null
let stripePromise = null
try {
  stripePromise = getStripe()
} catch (error) {
  console.error('Stripe initialization error:', error)
  stripeInitializationError = error
}

const ScheduledTaskPaymentForm = ({
  quote,
  subscriptionEstimate,
  selectedAnalysts,
  researchDepth,
  scheduleType,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleHour,
  scheduleMinute,
  onPaymentSuccess,
  loading,
  setLoading,
}) => {
  const [clientSecret, setClientSecret] = useState(null)
  const [loadingIntent, setLoadingIntent] = useState(true)

  console.log('ScheduledTaskPaymentForm render:', {
    quote,
    selectedAnalysts,
    researchDepth,
    scheduleType,
    scheduleDayOfWeek,
    scheduleDayOfMonth,
    scheduleHour,
    scheduleMinute,
    clientSecret,
    loadingIntent,
  })

  // Create SetupIntent on mount
  useEffect(() => {
    console.log('useEffect: Creating SetupIntent...')
    const setupIntent = async () => {
      try {
        setLoadingIntent(true)
        const response = await billingService.createSetupIntent()
        console.log('SetupIntent response:', response)
        setClientSecret(response.clientSecret)
      } catch (error) {
        console.error('Failed to create SetupIntent:', error)
        setClientSecret(null)
      } finally {
        setLoadingIntent(false)
      }
    }

    setupIntent()
  }, [])

  const elementsOptions = useMemo(() => {
    if (!clientSecret) {
      return null
    }
    return {
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#4338ca',
          borderRadius: '12px',
        },
      },
      locale: 'en',
    }
  }, [clientSecret])

  if (stripeInitializationError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Stripe is not configured"
        description={
          <Space direction="vertical" size="small">
            <Text>{stripeInitializationError.message}</Text>
          </Space>
        }
      />
    )
  }

  if (loadingIntent) {
    return (
      <div className="scheduled-payment-loading" aria-live="polite">
        <div className="scheduled-payment-loading__summary">
          <div>
            <Text className="payment-summary__title">Billing Summary</Text>
            <Text type="secondary" className="scheduled-payment-loading__hint">
              Setting up secure monthly billing.
            </Text>
          </div>
          <Tag color="blue" className="payment-summary__badge">
            Secure Payment
          </Tag>
        </div>

        <div className="scheduled-payment-loading__rows">
          <span />
          <span />
          <span />
        </div>

        <div className="scheduled-payment-loading__method">
          <Spin size="small" />
          <Text>Preparing payment options...</Text>
        </div>

        <div className="scheduled-payment-loading__button" />
      </div>
    )
  }

  if (!clientSecret) {
    return (
      <Alert
        type="error"
        showIcon
        message="Payment Setup Failed"
        description="Unable to initialize payment. Please refresh and try again."
      />
    )
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm
        quote={quote}
        subscriptionEstimate={subscriptionEstimate}
        selectedAnalysts={selectedAnalysts}
        researchDepth={researchDepth}
        scheduleType={scheduleType}
        scheduleDayOfWeek={scheduleDayOfWeek}
        scheduleDayOfMonth={scheduleDayOfMonth}
        scheduleHour={scheduleHour}
        scheduleMinute={scheduleMinute}
        onPaymentSuccess={onPaymentSuccess}
        loading={loading}
        setLoading={setLoading}
      />
    </Elements>
  )
}

export default ScheduledTaskPaymentForm
